import _ from "lodash";
import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import classNames from "classnames";
import {
  BentArrowTopLeftOutlined,
  SmileyHappyOutlined,
  DotsVerticalOutlined,
  AddOutlined,
  PencilLineOutlined,
  DeleteOutlined,
  MarkAsReadOutlined,
  ShareCurveOutlined,
  SettingsOutlined,
} from "@toddle-edu/ds-icons";
import { Popover } from "@toddle-edu/react-tiny-popover";
import {
  Button,
  Dropdown,
  DropdownMenu,
  IconButton,
  Dialog,
  Tooltip,
} from "@toddle-edu/ds-web";
import { elevations } from "@toddle-edu/ds-theme";

import { useI18n } from "Hooks";
import {
  MESSAGE_STATUS,
  getIsMessagingWidget,
  scrollOnlyIfNearBottom,
  isModifyMessageEnabled,
  recordMessagingMessageActions,
  isMessagingRevempEnabled,
  DATE_FORMATS,
  getChatContainer,
  MESSAGING_V2_WIDGET_WRAPPER_ID,
  getMessageLabelAndAttachmentsForReplyContainer,
  isMessagingReadReceiptEnabled,
  canAdminDeleteMessage,
  canStaffDeleteMessage,
  INTERACTION_TYPE,
  getIsViewMode,
} from "MessagingV2/modules/Utils";
import {
  toggleReaction,
  deleteMessage,
} from "MessagingV2/modules/MessagingModule";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";

import classes from "./MessageOptions.scss";
import { connect } from "react-redux";
import ReadReceiptModal from "MessagingV2/components/Channels/ReadReceiptModal";
import ReadReceipt from "MessagingV2/components/Messages/ReadReceipt";
import { formatDate } from "Utils";
import ForwardMessageContainer from "MessagingV2/components/Messages/ForwardMessageContainer";
import ACLStore from "lib/aclStore";
import {
  getDefaultNameSortPreference,
  getIsStaff,
} from "modules/CommonSelectors";
import { PollSettingsModal } from "MessagingV2/components/PollsAndForms/CreatePollModal/PollSettings";

const MESSAGE_ACTIONS = {
  REPLY: "REPLY",
  EDIT: "EDIT",
  DELETE: "DELETE",
};

export const DROPDOWN_TYPES = {
  MORE_OPTIONS: "MORE_OPTIONS",
  EMOJI_PANEL: "EMOJI_PANEL",
};

const MORE_OPTIONS_DROPDOWN_KEYS = {
  EDIT: "edit",
  DELETE: "delete",
  READ_RECEIPT: "read-receipt",
  POLL_SETTINGS: "poll-settings",
};

// Focus trap when any dropdown is open
const FocusTrapOverlay = () => {
  const { screenMode } = useChatOptions();
  const isMessagingWidget = getIsMessagingWidget({ screenMode });

  // for messaging widget, we need to append the overlay inside
  // the it, to prevent z-index stacking context issues
  const portalNode = isMessagingWidget
    ? document.getElementById(MESSAGING_V2_WIDGET_WRAPPER_ID)
    : document.body;

  // Although will never happen, but just to be safe
  if (!portalNode) return null;

  return createPortal(
    <div className={classes.preventScrollOverlay} />,
    portalNode
  );
};

const PANEL_EMOJIS = [
  {
    id: "+1",
    colons: ":+1:",
    unified: "1f44d",
    native: "👍",
  },
  {
    id: "innocent",
    colons: ":innocent:",
    unified: "1f607",
    native: "😇",
  },
  {
    id: "joy",
    colons: ":joy:",
    unified: "1f602",
    native: "😂",
  },
  {
    id: "-1",
    colons: ":-1:",
    unified: "1f44e",
    native: "👎",
  },
  {
    id: "clap",
    colons: ":clap:",
    unified: "1f44f",
    native: "👏",
  },
];

const getCanDeleteMessage = ({ canManageChannel }) => {
  const isStaffAllowedToDeleteMessages =
    canStaffDeleteMessage() && canManageChannel;

  return isStaffAllowedToDeleteMessages || canAdminDeleteMessage();
};

const getDropdownOptionsConfig = t => [
  {
    key: MORE_OPTIONS_DROPDOWN_KEYS.EDIT,
    label: t("common:edit"),
    isOptionHidden: ({ isMessageSent, isSender, isInteractiveMessage }) =>
      isInteractiveMessage ||
      !isSender ||
      !isMessageSent ||
      !isModifyMessageEnabled(),
    icon: <PencilLineOutlined />,
  },
  {
    key: MORE_OPTIONS_DROPDOWN_KEYS.READ_RECEIPT,
    label: t("common:read_receipts"),
    isOptionHidden: ({ isMessageSent, isChannel, isSender }) =>
      !isSender ||
      !isMessageSent ||
      !isChannel ||
      !isMessagingReadReceiptEnabled(),
    icon: <MarkAsReadOutlined />, // TODO: change icon
  },

  {
    key: MORE_OPTIONS_DROPDOWN_KEYS.POLL_SETTINGS,
    label: t("common:edit_with_label", {
      label: t("common:poll_settings"),
    }),
    isOptionHidden: ({ interactionType, isSender }) =>
      !(isSender && _.isEqual(INTERACTION_TYPE.POLL, interactionType)),
    icon: <SettingsOutlined />,
  },
  {
    key: "Divider",
    isDivider: true,
    isOptionHidden: optionsState => {
      const {
        isMessageSent,
        isSender,
        isInteractiveMessage,
        isChannel,
        canManageChannel,
      } = optionsState;

      // Logic from EDIT option's isOptionHidden
      const isEditHidden =
        isInteractiveMessage ||
        !isSender ||
        !isMessageSent ||
        !isModifyMessageEnabled();

      // Logic from READ_RECEIPT option's isOptionHidden
      const isReadReceiptHidden =
        !isSender ||
        !isMessageSent ||
        !isChannel ||
        !isMessagingReadReceiptEnabled();

      // Logic from DELETE option's isOptionHidden
      const canDeleteMessage = getCanDeleteMessage({ canManageChannel });
      const canModifyMessage = isModifyMessageEnabled();
      const isDeleteHidden = isSender
        ? !canDeleteMessage && !canModifyMessage
        : !canDeleteMessage;

      const isPollSettingEnable =
        _.isEqual(INTERACTION_TYPE.POLL, optionsState.interactionType) &&
        isSender;
      // Divider should be hidden if:
      // 1. Both Edit and Read Receipt are hidden
      // OR
      // 2. Delete is hidden
      return (
        isPollSettingEnable ||
        (isEditHidden && isReadReceiptHidden) ||
        isDeleteHidden
      );
    },
  },

  {
    key: MORE_OPTIONS_DROPDOWN_KEYS.DELETE,
    isOptionHidden: ({ isSender, canManageChannel }) => {
      const canDeleteMessage = getCanDeleteMessage({ canManageChannel });
      const canModifyMessage = isModifyMessageEnabled();

      return isSender
        ? !canDeleteMessage && !canModifyMessage
        : !canDeleteMessage;
    },
    label: t("common:delete"),
    isDestructive: true,
    icon: <DeleteOutlined />,
  },
];

const getDropdownOptions = ({
  t,
  isMessageSent,
  isChannel,
  isInteractiveMessage,
  interactionType,
  isSender,
  canManageChannel,
}) => {
  const dropdownOptionsConfig = getDropdownOptionsConfig(t);
  const options = _.reduce(
    dropdownOptionsConfig,
    (acc, option) => {
      const { isOptionHidden, ...rest } = option;
      const isHidden = _.isFunction(isOptionHidden)
        ? isOptionHidden({
            isMessageSent,
            isChannel,
            isSender,
            isInteractiveMessage,
            interactionType,
            canManageChannel,
          })
        : false;

      if (isHidden) return acc;

      if (option.isDivider) acc.push(option);
      else acc.push(rest);

      return acc;
    },
    []
  );

  return options;
};
const MessageOptions = props => {
  const {
    isSender,
    isChannel,
    message,
    toggleReaction,
    openedDropdown,
    setOpenedDropdown,
    onEditMessageClick,
    onReplyMessageClick,
    deleteMessage,
    setMainEmojiPickerState,
    status,
    getMessageElementId,
    shouldSeparateMessage = false,
    isInteractiveMessage = false,
    personalChatReceiverUserId,
    canManageChannel = false,
  } = props;

  const isMessageSent = _.isUndefined(status);
  const isUIRevempEnabled = isMessagingRevempEnabled();

  const { t } = useI18n(["common", "classRoom"]);
  const { chatId, screenMode } = useChatOptions();
  const isMessagingWidget = getIsMessagingWidget({ screenMode });
  const isViewMode = getIsViewMode({ screenMode });

  const containerRef = useRef(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isReadReceiptModalOpen, setIsReadReceiptModalOpen] = useState(false);
  const [isForwardMessageModalOpen, setIsForwardMessageModalOpen] = useState(
    false
  );
  const [isPollSettingModalOpen, setIsPollSettingModalOpen] = useState(false);

  const handleReplyClick = () => {
    onReplyMessageClick({ message });
    scrollOnlyIfNearBottom({ chatId });
    recordMessagingMessageActions({
      type: MESSAGE_ACTIONS.REPLY,
      messageId: message.id,
    });
  };

  const showForwardMessageOption = ACLStore.can("FeatureFlag:ForwardMessage");

  const canForwardMessage =
    showForwardMessageOption &&
    !isViewMode &&
    (!isInteractiveMessage || (isInteractiveMessage && getIsStaff()));

  const closeOpenedDropdown = () => setOpenedDropdown(null);

  // function to open mini emoji panel
  const toggleEmojiPanel = () => {
    setOpenedDropdown(prev =>
      prev === DROPDOWN_TYPES.EMOJI_PANEL ? null : DROPDOWN_TYPES.EMOJI_PANEL
    );
  };

  // function to open main emoji picker
  const openMainEmojiPicker = e => {
    /**
      Note: Do not remove the following line.
      It is required to prevent the the click event from bubbling up to the container,
      which would otherwise close the emoji popover. Since the button triggering
      the event would be destroyed before the event reaches the container,
      the dropdown would incorrectly interpret the click as occurring outside of it.
    */
    e.stopPropagation();

    closeOpenedDropdown();
    setMainEmojiPickerState({ message, element: containerRef.current });
  };

  const handleReadReceiptModalClose = () => {
    setIsReadReceiptModalOpen(false);
    closeOpenedDropdown();
  };

  const handleMoreOptionsVisibilityChange = visible => {
    setOpenedDropdown(visible ? DROPDOWN_TYPES.MORE_OPTIONS : null);
  };

  const closeDeleteDialog = () => {
    setIsDeleteModalOpen(false);
    closeOpenedDropdown();
  };

  const onConfirmDelete = () => {
    deleteMessage({ chatId, messageId: message.id });
    closeDeleteDialog();
  };

  const toggleForwardMessageContainer = () => {
    setIsForwardMessageModalOpen(state => !state);
  };

  const onMoreOptionsClick = ({ key }) => {
    switch (key) {
      case MORE_OPTIONS_DROPDOWN_KEYS.EDIT:
        onEditMessageClick({ message });
        closeOpenedDropdown();
        recordMessagingMessageActions({
          type: MESSAGE_ACTIONS.EDIT,
          messageId: message.id,
        });
        break;
      case MORE_OPTIONS_DROPDOWN_KEYS.DELETE:
        setIsDeleteModalOpen(true);
        recordMessagingMessageActions({
          type: MESSAGE_ACTIONS.DELETE,
          messageId: message.id,
        });
        break;
      case MORE_OPTIONS_DROPDOWN_KEYS.POLL_SETTINGS:
        setIsPollSettingModalOpen(true);
        closeOpenedDropdown();
        break;
      case MORE_OPTIONS_DROPDOWN_KEYS.READ_RECEIPT:
        setIsReadReceiptModalOpen(true);
        break;
      default:
        break;
    }
  };

  // handle emoji click on mini emoji panel
  const handleReactionClick = e => {
    const idx = _.get(e, "currentTarget.dataset.idx");
    const emoji = PANEL_EMOJIS[idx];

    toggleReaction({ messageId: message.id, emojiObject: emoji, chatId });
    closeOpenedDropdown();
  };

  const reactionPanelContent = (
    <div
      className={
        isUIRevempEnabled
          ? classes.popoverInnerContainerV2
          : classes.popoverInnerContainer
      }
      style={elevations["elevation-2B"]}
    >
      {_.map(PANEL_EMOJIS, (emoji, idx) => (
        <Button
          key={emoji.id}
          size={isUIRevempEnabled ? "small" : "medium"}
          variant="plain"
          className={
            isUIRevempEnabled ? classes.emojiButtonV2 : classes.emojiButton
          }
          data-idx={idx}
          onClick={handleReactionClick}
        >
          <div className={isUIRevempEnabled ? "heading-6" : "heading-3"}>
            {emoji.native}
          </div>
        </Button>
      ))}
      <IconButton
        size={isUIRevempEnabled ? "small" : "medium"}
        variant="plain"
        className={
          isUIRevempEnabled ? classes.emojiButtonV2 : classes.emojiButton
        }
        icon={
          <AddOutlined size={isUIRevempEnabled ? "xxx-small" : "x-small"} />
        }
        onClick={openMainEmojiPicker}
      />
    </div>
  );

  const containerClasses = classNames(classes.container, {
    [classes.receiverContainer]: !isSender,
  });

  const commonIconContainerProps = {
    size: isMessagingWidget || isUIRevempEnabled ? "x-small" : "small",
    variant: "plain",
    className: isUIRevempEnabled
      ? classes.iconContainerV2
      : classes.iconContainer,
  };

  const iconSize =
    isMessagingWidget || isUIRevempEnabled ? "xxx-small" : "xx-small";

  const attachment = _.get(message, "attachments", {});
  const firstAttachment = _.first(attachment);
  const interactionType = _.get(firstAttachment, "type");

  const interaction = _.get(firstAttachment, "interaction", {});
  const dropdownOptions = getDropdownOptions({
    t,
    isMessageSent,
    isChannel,
    isInteractiveMessage,
    interactionType,
    isSender,
    canManageChannel,
  });

  const dateClass = classNames("text-body-3", classes.date);
  const showDateAndReceipt = isUIRevempEnabled && !shouldSeparateMessage;
  const scrollContainer = getChatContainer(chatId);
  const isMoreOptionsVisible = openedDropdown === DROPDOWN_TYPES.MORE_OPTIONS;
  const isReactionsPanelOpen = openedDropdown === DROPDOWN_TYPES.EMOJI_PANEL;

  const canShowReadReceipt = isMessagingReadReceiptEnabled() && !isViewMode;
  const canDeleteMessage = getCanDeleteMessage({ canManageChannel });
  const showMoreOptionDropdown = canDeleteMessage || isSender;

  const canReactToMessage = isMessageSent && !isViewMode;
  const canReplyToMessage = isMessageSent && !isViewMode;

  const onClosePollSettingModal = () => {
    setIsPollSettingModalOpen(false);
    closeOpenedDropdown();
  };

  return (
    <div
      ref={containerRef}
      className={isUIRevempEnabled ? classes.containerV2 : containerClasses}
    >
      {showDateAndReceipt && isMessageSent ? (
        <div className={classes.dateReceiptContainer}>
          <div className={dateClass}>
            {formatDate({
              date: message?.createdAt,
              format: DATE_FORMATS.SENT_MESSAGE,
            })}
          </div>
          {canShowReadReceipt ? (
            <ReadReceipt
              isSender={isSender}
              isSent={isMessageSent}
              isRead={message?.isRead}
            />
          ) : null}
        </div>
      ) : null}

      {canReplyToMessage ? (
        <Tooltip
          placement="top"
          tooltip={<div className="text-tooltip">{t("common:reply")}</div>}
        >
          <IconButton
            {...commonIconContainerProps}
            onClick={handleReplyClick}
            aria-label="reply message"
            icon={<BentArrowTopLeftOutlined size={iconSize} variant="subtle" />}
          />
        </Tooltip>
      ) : null}

      {canForwardMessage ? (
        <Tooltip placement="top" tooltip={t("common:forward")}>
          <IconButton
            {...commonIconContainerProps}
            icon={<ShareCurveOutlined variant="subtle" />}
            onClick={toggleForwardMessageContainer}
          />
        </Tooltip>
      ) : null}

      {canReactToMessage ? (
        <Popover
          reposition
          isOpen={isReactionsPanelOpen}
          content={reactionPanelContent}
          positions={["top", "bottom"]}
          containerParent={scrollContainer}
          align={["start", "end"]}
          containerClassName={classes.popoverContainer}
          onClickOutside={closeOpenedDropdown}
        >
          <div>
            <Tooltip
              placement="top"
              tooltip={
                <div className="text-tooltip">{t("common:add_reaction")}</div>
              }
            >
              <IconButton
                {...commonIconContainerProps}
                onClick={toggleEmojiPanel}
                aria-label="open reactions panel"
                icon={<SmileyHappyOutlined size={iconSize} variant="subtle" />}
              />
            </Tooltip>
          </div>
        </Popover>
      ) : null}

      {showMoreOptionDropdown && !_.isEmpty(dropdownOptions) ? (
        <Dropdown
          visible={isMoreOptionsVisible}
          onVisibleChange={handleMoreOptionsVisibilityChange}
          trigger={["click"]}
          getPopupContainer={() =>
            document.getElementById(getMessageElementId(message))
          }
          {...(isUIRevempEnabled ? { align: { offset: [0, 8] } } : {})}
          overlay={
            <DropdownMenu
              dsVersion="2.0"
              options={dropdownOptions}
              onClick={onMoreOptionsClick}
            />
          }
        >
          <div>
            <Tooltip
              placement="top"
              tooltip={<div className="text-tooltip">{t("common:more")}</div>}
            >
              <IconButton
                {...commonIconContainerProps}
                aria-label="more options"
                icon={<DotsVerticalOutlined size={iconSize} variant="subtle" />}
              />
            </Tooltip>
          </div>
        </Dropdown>
      ) : null}

      {isForwardMessageModalOpen ? (
        <ForwardMessageContainer
          onClose={toggleForwardMessageContainer}
          message={getMessageLabelAndAttachmentsForReplyContainer({ message })}
          personalChatReceiverUserId={personalChatReceiverUserId}
        />
      ) : null}

      {isPollSettingModalOpen ? (
        <PollSettingsModal
          onCloseModal={onClosePollSettingModal}
          interaction={interaction}
          messageId={message.id}
        />
      ) : null}

      {isDeleteModalOpen ? (
        <Dialog
          isOpen
          title={t("common:delete_message")}
          message={t("classRoom:delete_message_alert")}
          variant="delete-dialog"
          onClose={closeDeleteDialog}
          onButtonOneClick={closeDeleteDialog}
          onButtonTwoClick={onConfirmDelete}
        />
      ) : null}
      {isReadReceiptModalOpen ? (
        <ReadReceiptModal
          onCloseModal={handleReadReceiptModalClose}
          message={message}
        />
      ) : null}
      {isMoreOptionsVisible || isReactionsPanelOpen ? (
        <FocusTrapOverlay />
      ) : null}
    </div>
  );
};

MessageOptions.propTypes = {
  message: PropTypes.object.isRequired,
  isSender: PropTypes.bool.isRequired,
  isChannel: PropTypes.bool.isRequired,
  openedDropdown: PropTypes.oneOf(_.values(DROPDOWN_TYPES)),
  toggleReaction: PropTypes.func.isRequired,
  setOpenedDropdown: PropTypes.func.isRequired,
  onEditMessageClick: PropTypes.func.isRequired,
  onReplyMessageClick: PropTypes.func.isRequired,
  deleteMessage: PropTypes.func.isRequired,
  getMessageElementId: PropTypes.func.isRequired,
  setMainEmojiPickerState: PropTypes.func.isRequired,
  status: PropTypes.oneOf(_.values(MESSAGE_STATUS)),
};

MessageOptions.defaultProps = {
  openedDropdown: null,
};

const mapDispatchToProps = {
  toggleReaction,
  deleteMessage,
};

const mapStateToProps = state => ({
  defaultStudentNameSortPreference: getDefaultNameSortPreference(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(MessageOptions);
